import type { GameState, BetType } from '@shared/gameTypes';
import { getCompletedHolesInOrder, getFrontHoles, getBackHoles, getPlayingOrder, getLastPlayedHole } from './golfEngine';
import { getGameEngine, isPressCapable } from '@/games/index';

export interface ConsolidatedGroup {
  betType: BetType;
  side: 'top' | 'bottom';
  mainPressMax: number;
  pressOnPressOptions: Array<{ pressId: string; label: string }>;
  holeOverride?: number;
}

/**
 * Single source of truth for "what press actions are available right now",
 * grouped by bet type + side so callers never have to guess which bet type
 * a generic "Press" action would apply to.
 *
 * Shared by PressRows.tsx (Bet Status "Presses" section) and
 * HoleDetailModal.tsx (per-hole carousel) so both surfaces always agree on
 * what's pressable and never silently collapse two distinct bet types
 * (e.g. a Back catch-up press and an Overall press both being eligible at
 * the turn) into a single ambiguous action.
 */
export function computeConsolidatedPressGroups(
  gameState: GameState,
  selectedHole: number | null
): ConsolidatedGroup[] {
  const engine = getGameEngine(gameState.gameType);
  const pressEng = isPressCapable(engine) ? engine : null;

  if (gameState.gameMode !== 'OnDemandPress' || !pressEng) return [];

  const completedHoles = getCompletedHolesInOrder(gameState);
  const frontHoles = getFrontHoles(gameState.startingHole);
  const backHoles = getBackHoles(gameState.startingHole);

  const isFrontComplete = frontHoles.every(h => completedHoles.includes(h));
  const isBackComplete = backHoles.every(h => completedHoles.includes(h));

  const anyBackHolesPlayed = backHoles.some(h => completedHoles.includes(h));
  const isAtCatchUpPoint = isFrontComplete && !anyBackHolesPlayed;

  const playingOrder = getPlayingOrder(gameState.startingHole);
  const lastHole = playingOrder[17];
  const secondToLastHole = playingOrder[16];
  const isHole17Complete = completedHoles.includes(secondToLastHole);
  const hole18HasScore = gameState.scores.some(s => s.holeNumber === lastHole && s.grossScore !== null);
  const lastHoleBetType: BetType = backHoles.includes(lastHole) ? 'Back' : 'Front';
  const isAtDesperationPoint = isHole17Complete && !hole18HasScore;

  const lastPlayedHole = getLastPlayedHole(gameState);
  const contextHole = selectedHole ?? lastPlayedHole;
  const selectedIsInFront = contextHole !== null && frontHoles.includes(contextHole);
  const selectedIsInBack = contextHole !== null && backHoles.includes(contextHole);

  const betTypes: BetType[] = ['Front', 'Back', 'Overall'];
  const sides: Array<'top' | 'bottom'> = ['top', 'bottom'];

  const pressOnPressCount: Record<string, number> = {};
  const availablePressOnPress: Array<{ pressId: string; side: 'top' | 'bottom'; label: string; betType: BetType }> = [];

  for (const press of gameState.presses) {
    if (press.betType === 'Front' && (isFrontComplete || !selectedIsInFront) && !(isAtDesperationPoint && lastHoleBetType === 'Front')) continue;
    if (press.betType === 'Back' && (isBackComplete || !selectedIsInBack) && !isAtCatchUpPoint && !(isAtDesperationPoint && lastHoleBetType === 'Back')) continue;

    for (const side of sides) {
      if (pressEng.canPressExistingPress(gameState, press.id, side)) {
        const betLabel = press.betType === 'Front' ? 'F' : press.betType === 'Back' ? 'B' : 'Over';
        const pressIndex = gameState.presses.filter(p => p.betType === press.betType).indexOf(press) + 1;
        availablePressOnPress.push({
          pressId: press.id,
          side,
          label: `P${pressIndex} ${betLabel}`,
          betType: press.betType,
        });
        const key = `${press.betType}-${side}`;
        pressOnPressCount[key] = (pressOnPressCount[key] || 0) + 1;
      }
    }
  }

  const availablePresses: Array<{ betType: BetType; side: 'top' | 'bottom'; label: string; maxPresses: number }> = [];

  for (const betType of betTypes) {
    if (betType === 'Front' && (isFrontComplete || !selectedIsInFront) && !(isAtDesperationPoint && lastHoleBetType === 'Front')) continue;
    if (betType === 'Back' && (isBackComplete || (!selectedIsInBack && !isAtCatchUpPoint)) && !(isAtDesperationPoint && lastHoleBetType === 'Back')) continue;

    for (const side of sides) {
      let effectiveSelectedHole = selectedHole;
      if (betType === 'Back' && isAtCatchUpPoint) {
        effectiveSelectedHole = backHoles[0];
      }
      if (isAtDesperationPoint && betType === lastHoleBetType) {
        effectiveSelectedHole = lastHole;
      }
      const maxPresses = pressEng.getMaxPressesAllowed(gameState, betType, side, effectiveSelectedHole);

      if (maxPresses > 0) {
        let label: string;
        if (betType === 'Front') {
          label = 'Front Press';
        } else if (betType === 'Back') {
          label = 'Back Press';
        } else {
          label = 'Overall Press';
        }
        availablePresses.push({
          betType,
          side,
          label,
          maxPresses,
        });
      }
    }
  }

  const consolidatedGroups: ConsolidatedGroup[] = [];

  for (const betType of betTypes) {
    for (const side of sides) {
      const mainPress = availablePresses.find(p => p.betType === betType && p.side === side);
      const popOptions = availablePressOnPress.filter(p => p.betType === betType && p.side === side);

      const mainMax = mainPress?.maxPresses ?? 0;
      const totalOptions = mainMax + popOptions.length;

      if (totalOptions > 0) {
        let holeOvr: number | undefined;
        if (isAtDesperationPoint && betType === lastHoleBetType) {
          holeOvr = lastHole;
        } else if (betType === 'Back' && isAtCatchUpPoint) {
          holeOvr = backHoles[0];
        }
        consolidatedGroups.push({
          betType,
          side,
          mainPressMax: mainMax,
          pressOnPressOptions: popOptions,
          holeOverride: holeOvr,
        });
      }
    }
  }

  return consolidatedGroups;
}
